// Frontend mirror of the backend password policy (JMS candidate portal).
//
// Source of truth lives in ems-api:
//   - PASSWORD_STRONG regex: src/const/core/validation.constants.ts
//   - length limits:         src/const/validation.constants.ts (PASSWORD_MIN/MAX)
//
// Backend pattern:
//   /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d\s])\S+$/
// lower + upper + digit + one VISIBLE (non-whitespace) special character, with
// no whitespace anywhere, 8–128 chars. Keep these rules in sync so the form
// never submits a password the API will reject with a 400.

export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 128;

export interface PasswordRule {
  readonly id: string;
  readonly label: string;
  readonly test: (password: string) => boolean;
}

export const PASSWORD_RULES: readonly PasswordRule[] = [
  {
    id: "length",
    label: `${PASSWORD_MIN_LENGTH}–${PASSWORD_MAX_LENGTH} characters`,
    test: (password) =>
      password.length >= PASSWORD_MIN_LENGTH &&
      password.length <= PASSWORD_MAX_LENGTH,
  },
  { id: "lowercase", label: "One lowercase letter", test: (password) => /[a-z]/.test(password) },
  { id: "uppercase", label: "One uppercase letter", test: (password) => /[A-Z]/.test(password) },
  { id: "number", label: "One number", test: (password) => /\d/.test(password) },
  {
    id: "special",
    label: "One special character",
    // Matches the backend `[^A-Za-z\d\s]` — a visible (non-whitespace) symbol.
    test: (password) => /[^A-Za-z0-9\s]/.test(password),
  },
];

export interface PasswordCheck extends PasswordRule {
  readonly met: boolean;
}

export function evaluatePassword(password: string): PasswordCheck[] {
  return PASSWORD_RULES.map((rule) => ({ ...rule, met: rule.test(password) }));
}

/** Whitespace is not permitted anywhere in a password (matches the backend `\S+$`). */
export function hasWhitespace(password: string): boolean {
  return /\s/.test(password);
}

export function isPasswordValid(password: string): boolean {
  return !hasWhitespace(password) && PASSWORD_RULES.every((rule) => rule.test(password));
}

export type PasswordStrengthLevel = "empty" | "weak" | "fair" | "good" | "strong";

export interface PasswordStrengthResult {
  readonly score: number; // 0..4
  readonly level: PasswordStrengthLevel;
  readonly label: string;
}

const STRENGTH_LABELS: Record<PasswordStrengthLevel, string> = {
  empty: "",
  weak: "Weak",
  fair: "Fair",
  good: "Good",
  strong: "Strong",
};

export function getPasswordStrength(password: string): PasswordStrengthResult {
  if (!password) return { score: 0, level: "empty", label: STRENGTH_LABELS.empty };

  const satisfied = PASSWORD_RULES.filter((rule) => rule.test(password)).length;
  const isLong = password.length >= 12;

  let level: PasswordStrengthLevel;
  let score: number;
  if (satisfied <= 2) {
    level = "weak";
    score = 1;
  } else if (satisfied <= 4) {
    level = "fair";
    score = 2;
  } else if (!isLong) {
    level = "good";
    score = 3;
  } else {
    level = "strong";
    score = 4;
  }

  return { score, level, label: STRENGTH_LABELS[level] };
}
